import React, { useRef, Children } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Check, Star, Sun, Moon, Heart, Sparkles, Activity, Shield, Clock, Video, Layers, Package, User, Eye, FileText, HelpCircle, ChevronRight, MessageCircle, Zap } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Breadcrumbs } from '../components/ui/Breadcrumbs';
import { Accordion } from '../components/ui/Accordion';
import { useCart } from '../contexts/CartContext';
interface PricingPageProps {
  onNavigate: (page: string) => void;
}
const staggerContainer = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.08
    }
  }
};
const staggerItem = {
  hidden: {
    opacity: 0,
    y: 24
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.45,
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  }
};
function AnimatedSection({
  children,
  className = ''
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <motion.section className={className} initial="hidden" whileInView="visible" viewport={{
    once: true,
    margin: '-60px'
  }} variants={{
    hidden: {
      opacity: 0,
      y: 30
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    }
  }} data-id="element-21631">
      {children}
    </motion.section>;
}
export function PricingPage({
  onNavigate
}: PricingPageProps) {
  const servicesRef = useRef<HTMLDivElement>(null);
  const {
    addItem
  } = useCart();
  const handleBuyPackage = (pkg: {
    title: string;
    desc: string;
    price: number;
    oldPrice?: number;
  }) => {
    addItem({
      id: `pkg-${Date.now()}`,
      name: pkg.title,
      description: pkg.desc,
      price: pkg.price,
      type: 'package',
      format: 'PDF-отчёты',
      duration: 'Бессрочно',
      oldPrice: pkg.oldPrice,
      badges: [{
        label: 'Пакет отчётов',
        variant: 'success'
      }]
    });
    onNavigate('checkout');
  };
  return <div className="w-full bg-slate-50 min-h-screen" data-id="element-21632">
      {/* 1. HERO */}
      <div className="relative bg-slate-900 text-white overflow-hidden pt-6 pb-14 lg:pb-16" data-id="element-21633">
        <div className="absolute top-0 right-0 -translate-y-1/4 translate-x-1/4 w-[800px] h-[800px] bg-indigo-500/20 rounded-full blur-[120px] pointer-events-none" data-id="element-21634" />
        <div className="absolute bottom-0 left-0 translate-y-1/3 -translate-x-1/4 w-[600px] h-[600px] bg-violet-500/15 rounded-full blur-[100px] pointer-events-none" data-id="element-21635" />
        <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_center,rgba(255,255,255,0.8)_1px,transparent_1px)] bg-[length:40px_40px] pointer-events-none" data-id="element-21636" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-21637">
          <Breadcrumbs items={[{
          label: 'Главная',
          onClick: () => onNavigate('home')
        }, {
          label: 'Цены'
        }]} className="mb-8 text-slate-400 [&_span]:text-slate-300 [&_button]:text-slate-400 [&_button:hover]:text-white [&_svg]:text-slate-500" data-id="element-21638" />

          <motion.div initial="hidden" animate="visible" variants={staggerContainer} className="max-w-3xl mx-auto text-center relative" data-id="element-21639">
            <motion.h1 variants={staggerItem} className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 leading-tight tracking-tight" data-id="element-21640">
              Выберите астрологический анализ
            </motion.h1>
            <motion.p variants={staggerItem} className="text-base sm:text-lg text-slate-300 mb-3 leading-relaxed max-w-2xl mx-auto" data-id="element-21641">
              Получите персональный астрологический разбор личности, отношений и
              жизненных периодов.
            </motion.p>
            <motion.p variants={staggerItem} className="text-sm text-slate-400 mb-7 max-w-xl mx-auto bg-white/5 py-2 px-4 rounded-full inline-block backdrop-blur-sm border border-white/10" data-id="element-21642">
              Каждая услуга включает бесплатный Beta-результат и полный
              Premium-отчёт.
            </motion.p>
            <motion.div variants={staggerItem} className="flex flex-col items-center" data-id="element-21643">
              <Button size="lg" onClick={() => onNavigate('free-chart')} className="bg-indigo-500 hover:bg-indigo-600 shadow-lg shadow-indigo-500/30 w-full sm:w-auto" data-id="element-21644">
                Построить натальную карту
                <ArrowRight className="ml-2 w-5 h-5" data-id="element-21645" />
              </Button>
              <div className="flex flex-wrap justify-center gap-4 sm:gap-6 mt-6 text-xs sm:text-sm text-slate-400" data-id="element-21646">
                <div className="flex items-center" data-id="element-21647">
                  <User className="w-3.5 h-3.5 mr-1.5 text-indigo-400" data-id="element-21648" />
                  <span data-id="element-21649">10 000+ отчётов</span>
                </div>
                <div className="flex items-center" data-id="element-21650">
                  <Zap className="w-3.5 h-3.5 mr-1.5 text-amber-400" data-id="element-21651" />
                  <span data-id="element-21652">Мгновенный результат</span>
                </div>
                <div className="flex items-center" data-id="element-21653">
                  <Shield className="w-3.5 h-3.5 mr-1.5 text-emerald-400" data-id="element-21654" />
                  <span data-id="element-21655">Безопасная оплата</span>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* 2. КАК РАБОТАЕТ ПЛАТФОРМА */}
      <AnimatedSection className="py-12 bg-white" data-id="element-21656">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-21657">
          <div className="text-center mb-10" data-id="element-21658">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2" data-id="element-21659">
              Как работает платформа
            </h2>
            <p className="text-sm text-slate-600" data-id="element-21660">
              Простой путь от ввода данных до получения полного персонального
              разбора
            </p>
          </div>

          <div className="relative" data-id="element-21661">
            <div className="hidden md:block absolute top-10 left-[15%] right-[15%] h-0.5 border-t-2 border-dashed border-slate-200" data-id="element-21662" />
            <div className="md:hidden absolute top-10 bottom-10 left-[1.75rem] w-0.5 border-l-2 border-dashed border-slate-200" data-id="element-21663" />

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-6" data-id="element-21664">
              {[{
              icon: <User className="w-6 h-6 md:w-8 md:h-8 text-indigo-500" data-id="element-21665" />,
              num: 1,
              title: 'Введите данные рождения',
              desc: 'Укажите дату, время и место рождения для точного расчёта.'
            }, {
              icon: <Eye className="w-6 h-6 md:w-8 md:h-8 text-indigo-500" data-id="element-21666" />,
              num: 2,
              title: 'Получите бесплатный Beta-результат',
              desc: 'Мгновенный краткий анализ основных показателей.'
            }, {
              icon: <Sparkles className="w-6 h-6 md:w-8 md:h-8 text-indigo-500" data-id="element-21667" />,
              num: 3,
              title: 'Разблокируйте полный Premium-отчёт',
              desc: 'Подробный персональный разбор с рекомендациями.'
            }].map(step => <div key={step.num} className="relative flex md:flex-col items-start md:items-center" data-id="element-21668">
                  <div className="w-14 h-14 md:w-20 md:h-20 bg-white rounded-2xl md:rounded-full flex items-center justify-center mb-0 md:mb-4 border border-slate-100 shadow-sm relative z-10 flex-shrink-0 mr-4 md:mr-0" data-id="element-21669">
                    <div className="absolute -top-1.5 -right-1.5 md:top-0 md:right-0 w-5 h-5 md:w-6 md:h-6 bg-indigo-600 text-white text-[10px] md:text-xs font-bold rounded-full flex items-center justify-center md:translate-x-1/4 md:-translate-y-1/4 shadow-sm" data-id="element-21670">
                      {step.num}
                    </div>
                    {step.icon}
                  </div>
                  <div className="md:text-center pt-1 md:pt-0" data-id="element-21671">
                    <h3 className="text-sm md:text-base font-bold text-slate-900 mb-1" data-id="element-21672">
                      {step.title}
                    </h3>
                    <p className="text-xs md:text-sm text-slate-500" data-id="element-21673">
                      {step.desc}
                    </p>
                  </div>
                </div>)}
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* 3. РАЗОВЫЕ УСЛУГИ */}
      <div ref={servicesRef} className="scroll-mt-4" data-id="element-21674">
        <AnimatedSection className="py-12 bg-slate-50" data-id="element-21675">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-21676">
            <div className="text-center mb-10" data-id="element-21677">
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2" data-id="element-21678">
                Разовые астрологические услуги
              </h2>
              <p className="text-sm md:text-base text-slate-600 max-w-2xl mx-auto" data-id="element-21679">
                Выберите конкретный астрологический анализ и получите
                персональный отчёт.
              </p>
            </div>

            <motion.div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5" variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} data-id="element-21680">
              {/* Натальная карта */}
              <motion.div variants={staggerItem} data-id="element-21681">
                <Card padding="md" className="h-full flex flex-col border-slate-200 relative overflow-hidden group hover:border-indigo-200 transition-colors" data-id="element-21682">
                  <div className="absolute top-0 left-0 right-0 h-1 bg-indigo-500" data-id="element-21683" />
                  <div className="flex items-center gap-3 mb-3" data-id="element-21684">
                    <div className="w-10 h-10 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center" data-id="element-21685">
                      <Moon className="w-5 h-5" data-id="element-21686" />
                    </div>
                    <h3 className="text-lg font-bold text-slate-900" data-id="element-21687">
                      Натальная карта
                    </h3>
                  </div>
                  <p className="text-sm text-slate-600 mb-4" data-id="element-21688">
                    Анализ личности, характера, талантов и жизненного
                    потенциала.
                  </p>
                  <div className="bg-slate-50 rounded-lg p-3 mb-4 border border-slate-100" data-id="element-21689">
                    <div className="text-xs text-slate-500" data-id="element-21690">Premium отчёт</div>
                    <div className="text-2xl font-bold text-slate-900" data-id="element-21691">
                      3 900 ₽
                    </div>
                  </div>
                  <div className="mb-3" data-id="element-21692">
                    <div className="flex items-center justify-between mb-2" data-id="element-21693">
                      <span className="text-xs font-bold text-slate-400 uppercase tracking-wider" data-id="element-21694">
                        Beta включает:
                      </span>
                      <Badge variant="neutral" size="sm" data-id="element-21695">
                        Бесплатно
                      </Badge>
                    </div>
                    <ul className="space-y-1" data-id="element-21696">
                      {['Солнце', 'Луна', 'Асцендент', 'краткий психологический профиль'].map((t, i) => <li key={i} className="flex items-start text-xs text-slate-600" data-id="element-21697">
                          <Check className="w-3.5 h-3.5 text-slate-400 mr-2 mt-0.5 flex-shrink-0" data-id="element-21698" />
                          {t}
                        </li>)}
                    </ul>
                  </div>
                  <hr className="border-slate-100 mb-3" data-id="element-21699" />
                  <div className="mb-5 flex-grow" data-id="element-21700">
                    <div className="flex items-center justify-between mb-2" data-id="element-21701">
                      <span className="text-xs font-bold text-indigo-600 uppercase tracking-wider" data-id="element-21702">
                        Premium включает:
                      </span>
                      <span className="text-[10px] font-medium text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded" data-id="element-21703">
                        от 20 стр.
                      </span>
                    </div>
                    <ul className="space-y-1" data-id="element-21704">
                      {['полный анализ натальной карты', 'планеты в знаках', 'планеты в домах', 'аспекты', 'таланты и предназначение', 'рекомендации'].map((t, i) => <li key={i} className="flex items-start text-xs text-slate-700 font-medium" data-id="element-21705">
                          <Check className="w-3.5 h-3.5 text-indigo-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-21706" />
                          {t}
                        </li>)}
                    </ul>
                  </div>
                  <Button fullWidth onClick={() => onNavigate('service-natal')} data-id="element-21707">
                    Построить карту
                  </Button>
                </Card>
              </motion.div>

              {/* Соляр */}
              <motion.div variants={staggerItem} data-id="element-21708">
                <Card padding="md" className="h-full flex flex-col border-slate-200 relative overflow-hidden group hover:border-amber-200 transition-colors" data-id="element-21709">
                  <div className="absolute top-0 left-0 right-0 h-1 bg-amber-500" data-id="element-21710" />
                  <div className="flex items-center gap-3 mb-3" data-id="element-21711">
                    <div className="w-10 h-10 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center" data-id="element-21712">
                      <Sun className="w-5 h-5" data-id="element-21713" />
                    </div>
                    <h3 className="text-lg font-bold text-slate-900" data-id="element-21714">Соляр</h3>
                  </div>
                  <p className="text-sm text-slate-600 mb-4" data-id="element-21715">
                    Астрологический прогноз на личный год.
                  </p>
                  <div className="bg-slate-50 rounded-lg p-3 mb-4 border border-slate-100" data-id="element-21716">
                    <div className="text-xs text-slate-500" data-id="element-21717">Premium отчёт</div>
                    <div className="text-2xl font-bold text-slate-900" data-id="element-21718">
                      4 400 ₽
                    </div>
                  </div>
                  <div className="mb-3" data-id="element-21719">
                    <div className="flex items-center justify-between mb-2" data-id="element-21720">
                      <span className="text-xs font-bold text-slate-400 uppercase tracking-wider" data-id="element-21721">
                        Beta включает:
                      </span>
                      <Badge variant="neutral" size="sm" data-id="element-21722">
                        Бесплатно
                      </Badge>
                    </div>
                    <ul className="space-y-1" data-id="element-21723">
                      {['главная тема года', 'краткий прогноз'].map((t, i) => <li key={i} className="flex items-start text-xs text-slate-600" data-id="element-21724">
                          <Check className="w-3.5 h-3.5 text-slate-400 mr-2 mt-0.5 flex-shrink-0" data-id="element-21725" />
                          {t}
                        </li>)}
                    </ul>
                  </div>
                  <hr className="border-slate-100 mb-3" data-id="element-21726" />
                  <div className="mb-5 flex-grow" data-id="element-21727">
                    <div className="flex items-center justify-between mb-2" data-id="element-21728">
                      <span className="text-xs font-bold text-amber-600 uppercase tracking-wider" data-id="element-21729">
                        Premium включает:
                      </span>
                      <span className="text-[10px] font-medium text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded" data-id="element-21730">
                        от 15 стр.
                      </span>
                    </div>
                    <ul className="space-y-1" data-id="element-21731">
                      {['полный солярный анализ', 'ключевые сферы года', 'важные периоды', 'рекомендации'].map((t, i) => <li key={i} className="flex items-start text-xs text-slate-700 font-medium" data-id="element-21732">
                          <Check className="w-3.5 h-3.5 text-amber-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-21733" />
                          {t}
                        </li>)}
                    </ul>
                  </div>
                  <Button variant="outline" fullWidth onClick={() => onNavigate('service-solar')} data-id="element-21734">
                    Подробнее
                  </Button>
                </Card>
              </motion.div>

              {/* Комбо */}
              <motion.div variants={staggerItem} data-id="element-21735">
                <Card padding="md" className="h-full flex flex-col border-violet-300 ring-2 ring-violet-100 shadow-lg shadow-violet-500/10 relative overflow-hidden group" data-id="element-21736">
                  <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-violet-500 to-fuchsia-500" data-id="element-21737" />
                  <div className="absolute top-3 right-3 z-10" data-id="element-21738">
                    <Badge variant="pro" className="bg-violet-100 text-violet-700 border border-violet-200 text-[10px]" data-id="element-21739">
                      Самый популярный
                    </Badge>
                  </div>
                  <div className="flex items-center gap-3 mb-3 mt-1" data-id="element-21740">
                    <div className="w-10 h-10 rounded-lg bg-violet-100 text-violet-600 flex items-center justify-center" data-id="element-21741">
                      <Layers className="w-5 h-5" data-id="element-21742" />
                    </div>
                    <h3 className="text-lg font-bold text-slate-900 pr-20" data-id="element-21743">
                      Комбо: Натал + Соляр
                    </h3>
                  </div>
                  <p className="text-sm text-slate-600 mb-4" data-id="element-21744">
                    Полный анализ личности и прогноз на ближайший год.
                  </p>
                  <div className="bg-violet-50 rounded-lg p-3 mb-4 border border-violet-100" data-id="element-21745">
                    <div className="text-xs text-violet-600 font-medium" data-id="element-21746">
                      Premium отчёт
                    </div>
                    <div className="flex items-baseline gap-2" data-id="element-21747">
                      <span className="text-2xl font-bold text-slate-900" data-id="element-21748">
                        6 900 ₽
                      </span>
                      <span className="text-sm text-slate-400 line-through" data-id="element-21749">
                        8 300 ₽
                      </span>
                    </div>
                  </div>
                  <div className="mb-3" data-id="element-21750">
                    <div className="flex items-center justify-between mb-2" data-id="element-21751">
                      <span className="text-xs font-bold text-slate-400 uppercase tracking-wider" data-id="element-21752">
                        Beta включает:
                      </span>
                      <Badge variant="neutral" size="sm" data-id="element-21753">
                        Бесплатно
                      </Badge>
                    </div>
                    <ul className="space-y-1" data-id="element-21754">
                      {['краткий профиль личности', 'краткий прогноз года'].map((t, i) => <li key={i} className="flex items-start text-xs text-slate-600" data-id="element-21755">
                            <Check className="w-3.5 h-3.5 text-slate-400 mr-2 mt-0.5 flex-shrink-0" data-id="element-21756" />
                            {t}
                          </li>)}
                    </ul>
                  </div>
                  <hr className="border-slate-100 mb-3" data-id="element-21757" />
                  <div className="mb-5 flex-grow" data-id="element-21758">
                    <div className="flex items-center justify-between mb-2" data-id="element-21759">
                      <span className="text-xs font-bold text-violet-600 uppercase tracking-wider" data-id="element-21760">
                        Premium включает:
                      </span>
                      <span className="text-[10px] font-medium text-violet-600 bg-violet-100 px-1.5 py-0.5 rounded" data-id="element-21761">
                        от 35 стр.
                      </span>
                    </div>
                    <ul className="space-y-1" data-id="element-21762">
                      {['полный анализ натальной карты', 'полный соляр', 'рекомендации'].map((t, i) => <li key={i} className="flex items-start text-xs text-slate-700 font-medium" data-id="element-21763">
                          <Check className="w-3.5 h-3.5 text-violet-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-21764" />
                          {t}
                        </li>)}
                    </ul>
                  </div>
                  <Button variant="primary" fullWidth onClick={() => onNavigate('service-combo')} className="bg-violet-600 hover:bg-violet-700" data-id="element-21765">
                    Подробнее
                  </Button>
                </Card>
              </motion.div>

              {/* Совместимость */}
              <motion.div variants={staggerItem} data-id="element-21766">
                <Card padding="md" className="h-full flex flex-col border-slate-200 relative overflow-hidden group hover:border-rose-200 transition-colors" data-id="element-21767">
                  <div className="absolute top-0 left-0 right-0 h-1 bg-rose-400" data-id="element-21768" />
                  <div className="flex items-center gap-3 mb-3" data-id="element-21769">
                    <div className="w-10 h-10 rounded-lg bg-rose-50 text-rose-500 flex items-center justify-center" data-id="element-21770">
                      <Heart className="w-5 h-5" data-id="element-21771" />
                    </div>
                    <h3 className="text-lg font-bold text-slate-900" data-id="element-21772">
                      Совместимость (Синастрия)
                    </h3>
                  </div>
                  <p className="text-sm text-slate-600 mb-4" data-id="element-21773">
                    Анализ совместимости двух людей.
                  </p>
                  <div className="bg-slate-50 rounded-lg p-3 mb-4 border border-slate-100" data-id="element-21774">
                    <div className="text-xs text-slate-500" data-id="element-21775">Premium отчёт</div>
                    <div className="text-2xl font-bold text-slate-900" data-id="element-21776">
                      4 900 ₽
                    </div>
                  </div>
                  <div className="mb-3" data-id="element-21777">
                    <div className="flex items-center justify-between mb-2" data-id="element-21778">
                      <span className="text-xs font-bold text-slate-400 uppercase tracking-wider" data-id="element-21779">
                        Beta включает:
                      </span>
                      <Badge variant="neutral" size="sm" data-id="element-21780">
                        Бесплатно
                      </Badge>
                    </div>
                    <ul className="space-y-1" data-id="element-21781">
                      {['общий уровень совместимости', 'краткий анализ'].map((t, i) => <li key={i} className="flex items-start text-xs text-slate-600" data-id="element-21782">
                            <Check className="w-3.5 h-3.5 text-slate-400 mr-2 mt-0.5 flex-shrink-0" data-id="element-21783" />
                            {t}
                          </li>)}
                    </ul>
                  </div>
                  <hr className="border-slate-100 mb-3" data-id="element-21784" />
                  <div className="mb-5 flex-grow" data-id="element-21785">
                    <div className="flex items-center justify-between mb-2" data-id="element-21786">
                      <span className="text-xs font-bold text-rose-500 uppercase tracking-wider" data-id="element-21787">
                        Premium включает:
                      </span>
                      <span className="text-[10px] font-medium text-rose-600 bg-rose-50 px-1.5 py-0.5 rounded" data-id="element-21788">
                        от 25 стр.
                      </span>
                    </div>
                    <ul className="space-y-1" data-id="element-21789">
                      {['эмоциональная совместимость', 'романтическая динамика', 'конфликтные зоны', 'рекомендации'].map((t, i) => <li key={i} className="flex items-start text-xs text-slate-700 font-medium" data-id="element-21790">
                          <Check className="w-3.5 h-3.5 text-rose-400 mr-2 mt-0.5 flex-shrink-0" data-id="element-21791" />
                          {t}
                        </li>)}
                    </ul>
                  </div>
                  <Button variant="outline" fullWidth onClick={() => onNavigate('service-compatibility')} data-id="element-21792">
                    Подробнее
                  </Button>
                </Card>
              </motion.div>

              {/* Транзиты */}
              <motion.div variants={staggerItem} data-id="element-21793">
                <Card padding="md" className="h-full flex flex-col border-slate-200 relative overflow-hidden group hover:border-teal-200 transition-colors" data-id="element-21794">
                  <div className="absolute top-0 left-0 right-0 h-1 bg-teal-500" data-id="element-21795" />
                  <div className="flex items-center gap-3 mb-3" data-id="element-21796">
                    <div className="w-10 h-10 rounded-lg bg-teal-50 text-teal-600 flex items-center justify-center" data-id="element-21797">
                      <Activity className="w-5 h-5" data-id="element-21798" />
                    </div>
                    <h3 className="text-lg font-bold text-slate-900" data-id="element-21799">
                      Прогноз (Транзиты)
                    </h3>
                  </div>
                  <p className="text-sm text-slate-600 mb-4" data-id="element-21800">
                    Астрологический прогноз на ближайшие месяцы.
                  </p>
                  <div className="bg-slate-50 rounded-lg p-3 mb-4 border border-slate-100" data-id="element-21801">
                    <div className="text-xs text-slate-500" data-id="element-21802">Premium отчёт</div>
                    <div className="text-2xl font-bold text-slate-900" data-id="element-21803">
                      3 900 ₽
                    </div>
                  </div>
                  <div className="mb-3" data-id="element-21804">
                    <div className="flex items-center justify-between mb-2" data-id="element-21805">
                      <span className="text-xs font-bold text-slate-400 uppercase tracking-wider" data-id="element-21806">
                        Beta включает:
                      </span>
                      <Badge variant="neutral" size="sm" data-id="element-21807">
                        Бесплатно
                      </Badge>
                    </div>
                    <ul className="space-y-1" data-id="element-21808">
                      {['основной транзит', 'краткий прогноз'].map((t, i) => <li key={i} className="flex items-start text-xs text-slate-600" data-id="element-21809">
                          <Check className="w-3.5 h-3.5 text-slate-400 mr-2 mt-0.5 flex-shrink-0" data-id="element-21810" />
                          {t}
                        </li>)}
                    </ul>
                  </div>
                  <hr className="border-slate-100 mb-3" data-id="element-21811" />
                  <div className="mb-5 flex-grow" data-id="element-21812">
                    <div className="flex items-center justify-between mb-2" data-id="element-21813">
                      <span className="text-xs font-bold text-teal-600 uppercase tracking-wider" data-id="element-21814">
                        Premium включает:
                      </span>
                      <span className="text-[10px] font-medium text-teal-600 bg-teal-50 px-1.5 py-0.5 rounded" data-id="element-21815">
                        от 15 стр.
                      </span>
                    </div>
                    <ul className="space-y-1" data-id="element-21816">
                      {['полный анализ транзитов', 'ключевые даты', 'рекомендации'].map((t, i) => <li key={i} className="flex items-start text-xs text-slate-700 font-medium" data-id="element-21817">
                          <Check className="w-3.5 h-3.5 text-teal-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-21818" />
                          {t}
                        </li>)}
                    </ul>
                  </div>
                  <Button variant="outline" fullWidth onClick={() => onNavigate('service-forecast')} data-id="element-21819">
                    Подробнее
                  </Button>
                </Card>
              </motion.div>

              {/* Консультация */}
              <motion.div variants={staggerItem} data-id="element-21820">
                <Card padding="md" className="h-full flex flex-col border-slate-200 relative overflow-hidden group hover:border-sky-200 transition-colors" data-id="element-21821">
                  <div className="absolute top-0 left-0 right-0 h-1 bg-sky-500" data-id="element-21822" />
                  <div className="flex items-center gap-3 mb-3" data-id="element-21823">
                    <div className="w-10 h-10 rounded-lg bg-sky-50 text-sky-600 flex items-center justify-center" data-id="element-21824">
                      <Video className="w-5 h-5" data-id="element-21825" />
                    </div>
                    <h3 className="text-lg font-bold text-slate-900" data-id="element-21826">
                      Консультация с астрологом
                    </h3>
                  </div>
                  <p className="text-sm text-slate-600 mb-4" data-id="element-21827">
                    Персональная консультация с профессиональным астрологом.
                  </p>
                  <div className="bg-slate-50 rounded-lg p-3 mb-4 border border-slate-100" data-id="element-21828">
                    <div className="text-xs text-slate-500" data-id="element-21829">Личная встреча</div>
                    <div className="text-2xl font-bold text-slate-900" data-id="element-21830">
                      от 2 990 ₽
                    </div>
                  </div>
                  <div className="mb-3" data-id="element-21831">
                    <span className="text-xs font-bold text-slate-400 uppercase tracking-wider" data-id="element-21832">
                      Beta:
                    </span>
                    <p className="text-xs text-slate-400 italic mt-1" data-id="element-21833">
                      Для консультаций Beta отсутствует
                    </p>
                  </div>
                  <hr className="border-slate-100 mb-3" data-id="element-21834" />
                  <div className="mb-5 flex-grow" data-id="element-21835">
                    <span className="text-xs font-bold text-sky-600 uppercase tracking-wider" data-id="element-21836">
                      Формат:
                    </span>
                    <ul className="space-y-1 mt-2" data-id="element-21837">
                      {['онлайн-встреча по видеосвязи', 'персональный разбор карты', 'ответы на ваши вопросы'].map((t, i) => <li key={i} className="flex items-start text-xs text-slate-700 font-medium" data-id="element-21838">
                          <Check className="w-3.5 h-3.5 text-sky-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-21839" />
                          {t}
                        </li>)}
                    </ul>
                  </div>
                  <Button variant="outline" fullWidth onClick={() => onNavigate('service-consultation')} data-id="element-21840">
                    Подробнее
                  </Button>
                </Card>
              </motion.div>
            </motion.div>
          </div>
        </AnimatedSection>
      </div>

      {/* 5. TRUST / GUARANTEE */}
      <section className="py-8 bg-white border-y border-slate-100" data-id="element-21841">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-21842">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6" data-id="element-21843">
            {[{
            icon: <Clock className="w-5 h-5 text-indigo-600" data-id="element-21844" />,
            bg: 'bg-indigo-50',
            title: 'Мгновенный результат',
            desc: 'Отчёт готов сразу после оплаты'
          }, {
            icon: <Shield className="w-5 h-5 text-emerald-600" data-id="element-21845" />,
            bg: 'bg-emerald-50',
            title: 'Безопасная оплата',
            desc: 'Надежное шифрование данных'
          }, {
            icon: <FileText className="w-5 h-5 text-amber-600" data-id="element-21846" />,
            bg: 'bg-amber-50',
            title: 'PDF-отчёт навсегда',
            desc: 'Доступен в личном кабинете'
          }, {
            icon: <MessageCircle className="w-5 h-5 text-sky-600" data-id="element-21847" />,
            bg: 'bg-sky-50',
            title: 'Поддержка 24/7',
            desc: 'Поможем с любым вопросом'
          }].map((item, i) => <div key={i} className="flex flex-col items-center text-center" data-id="element-21848">
                <div className={`w-10 h-10 rounded-full ${item.bg} flex items-center justify-center mb-2`} data-id="element-21849">
                  {item.icon}
                </div>
                <h4 className="text-xs font-bold text-slate-900 mb-0.5" data-id="element-21850">
                  {item.title}
                </h4>
                <p className="text-[10px] text-slate-500 leading-tight" data-id="element-21851">
                  {item.desc}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* 6. НЕ ЗНАЕТЕ, ЧТО ВЫБРАТЬ? */}
      <AnimatedSection className="py-12 bg-slate-50" data-id="element-21852">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-21853">
          <div className="text-center mb-8" data-id="element-21854">
            <div className="w-11 h-11 rounded-xl bg-indigo-100 text-indigo-600 flex items-center justify-center mx-auto mb-3" data-id="element-21855">
              <HelpCircle className="w-5 h-5" data-id="element-21856" />
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2" data-id="element-21857">
              Не знаете, что выбрать?
            </h2>
            <p className="text-sm text-slate-600" data-id="element-21858">
              Выберите вашу главную цель, и мы подскажем подходящую услугу
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-id="element-21859">
            {[{
            goal: 'Хочу понять себя',
            service: 'Натальная карта',
            page: 'service-natal',
            icon: <Moon className="w-4 h-4" data-id="element-21860" />,
            color: 'text-indigo-600',
            borderHover: 'hover:border-indigo-300'
          }, {
            goal: 'Хочу прогноз на год',
            service: 'Соляр',
            page: 'service-solar',
            icon: <Sun className="w-4 h-4" data-id="element-21861" />,
            color: 'text-amber-600',
            borderHover: 'hover:border-amber-300'
          }, {
            goal: 'Хочу личность и прогноз',
            service: 'Комбо: Натал + Соляр',
            page: 'service-combo',
            icon: <Layers className="w-4 h-4" data-id="element-21862" />,
            color: 'text-violet-600',
            borderHover: 'hover:border-violet-300'
          }, {
            goal: 'Хочу понять отношения',
            service: 'Совместимость',
            page: 'service-compatibility',
            icon: <Heart className="w-4 h-4" data-id="element-21863" />,
            color: 'text-rose-600',
            borderHover: 'hover:border-rose-300'
          }, {
            goal: 'Хочу прогноз на ближайшее время',
            service: 'Транзиты',
            page: 'service-forecast',
            icon: <Activity className="w-4 h-4" data-id="element-21864" />,
            color: 'text-teal-600',
            borderHover: 'hover:border-teal-300'
          }, {
            goal: 'Хочу личный разбор',
            service: 'Консультация',
            page: 'service-consultation',
            icon: <Video className="w-4 h-4" data-id="element-21865" />,
            color: 'text-sky-600',
            borderHover: 'hover:border-sky-300'
          }].map((item, i) => <button key={i} onClick={() => onNavigate(item.page)} className={`flex items-center gap-3 bg-white rounded-xl px-4 py-3 border border-slate-200 text-left transition-all group hover:shadow-sm ${item.borderHover}`} data-id="element-21866">
                <div className={`w-9 h-9 rounded-lg bg-slate-50 ${item.color} flex items-center justify-center flex-shrink-0`} data-id="element-21867">
                  {item.icon}
                </div>
                <div className="flex-grow min-w-0" data-id="element-21868">
                  <div className="text-sm font-bold text-slate-900" data-id="element-21869">
                    {item.goal}
                  </div>
                  <div className="text-xs text-slate-500" data-id="element-21870">→ {item.service}</div>
                </div>
                <ChevronRight className="w-4 h-4 text-slate-300 flex-shrink-0 group-hover:text-slate-600 transition-colors" data-id="element-21871" />
              </button>)}
          </div>
        </div>
      </AnimatedSection>

      {/* 7. FAQ */}
      <AnimatedSection className="py-14 bg-white" data-id="element-21872">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-21873">
          <div className="text-center mb-8" data-id="element-21874">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2" data-id="element-21875">
              Часто задаваемые вопросы
            </h2>
          </div>
          <Accordion items={[{
          id: '1',
          title: 'Что такое Beta-результат?',
          content: 'Beta — это бесплатный предварительный анализ, который вы получаете сразу после ввода данных рождения. Он показывает основные астрологические показатели и позволяет познакомиться с форматом анализа перед покупкой полного отчёта.'
        }, {
          id: '2',
          title: 'Чем Premium отличается от Beta?',
          content: 'Premium — это полный подробный отчёт с разбором всех элементов карты, прогнозами и практическими рекомендациями. Объём Premium-отчёта значительно больше и включает глубокий персональный анализ.'
        }, {
          id: '3',
          title: 'Можно ли использовать пакет для разных услуг?',
          content: 'Да! Каждый отчёт из пакета можно использовать для любой Premium-услуги: натальная карта, соляр, совместимость или прогноз (транзиты). Вы сами выбираете, на что потратить каждый отчёт.'
        }, {
          id: '4',
          title: 'В каком формате приходит отчёт?',
          content: 'Все отчёты предоставляются в формате PDF. Вы можете скачать их на странице результата сразу после оплаты, а также получить на email. Отчёты доступны в вашем личном кабинете бессрочно.'
        }, {
          id: '5',
          title: 'Сколько времени занимает расчёт?',
          content: 'Автоматические отчёты генерируются мгновенно — сразу после оплаты. Консультация с астрологом назначается на удобное для вас время.'
        }]} data-id="element-21876" />
        </div>
      </AnimatedSection>

      {/* 8. ФИНАЛЬНЫЙ CTA */}
      <AnimatedSection className="py-16 bg-slate-900 relative overflow-hidden" data-id="element-21877">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#4f46e520,#8b5cf620)]" data-id="element-21878" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10" data-id="element-21879">
          <motion.div animate={{
          scale: [1, 1.1, 1]
        }} transition={{
          duration: 3,
          repeat: Infinity,
          ease: 'easeInOut'
        }} className="inline-block mb-5" data-id="element-21880">
            <Star className="w-8 h-8 text-indigo-400" data-id="element-21881" />
          </motion.div>
          <h2 className="text-2xl md:text-4xl font-bold text-white mb-4" data-id="element-21882">
            Начните исследовать свою натальную карту
          </h2>
          <p className="text-base text-indigo-100/80 mb-8 max-w-xl mx-auto" data-id="element-21883">
            Введите данные рождения и получите персональный астрологический
            анализ.
          </p>
          <Button size="lg" variant="outline" onClick={() => onNavigate('free-chart')} className="shadow-xl shadow-indigo-500/25 !bg-white !text-indigo-600 !border-white/30 hover:!bg-indigo-50" data-id="element-21884">
            Построить карту
            <ArrowRight className="ml-2 w-5 h-5" data-id="element-21885" />
          </Button>
          <div className="flex flex-wrap items-center justify-center gap-6 mt-8 text-sm text-indigo-200/60" data-id="element-21886">
            <span className="flex items-center" data-id="element-21887">
              <Shield className="w-4 h-4 mr-1.5 text-emerald-400/80" data-id="element-21888" />{' '}
              Безопасная оплата
            </span>
            <span className="flex items-center" data-id="element-21889">
              <Clock className="w-4 h-4 mr-1.5 text-indigo-300/80" data-id="element-21890" /> Мгновенный
              результат
            </span>
          </div>
        </div>
      </AnimatedSection>
    </div>;
}